import { useMemo } from 'react';
import { distinctArrayBy } from '@elementary/common-ui/utils/common';
import { TEST_RESULTS } from '@elementary/common-ui/constants/queryKeys';
import useGraphqlPagedQuery from '@elementary/common-ui/hooks/useGraphqlPagedQuery';
import { DEFAULT_DAYS_BACK, PAGE_SIZE } from '@elementary/common-ui/constants';
import {
  parseApiTestResultTableData,
  type ApiTestResultsTableData,
  type TestResultsTableData,
} from '@elementary/common-ui/components';
import {
  ApiTestResultsTableDataPagedResponse,
  getTestResultsTableDataQuery,
} from './api';
import useTestResultsQueryContext from './useTestResultsQueryContext';

const useTestResults = ({
  enabled = true,
  onError,
  search,
}: {
  enabled?: boolean;
  onError?: () => void;
  search?: string;
}) => {
  const {
    envid,
    selectedAssetIds,
    selectedTestIds,
    selectedIdsHash,
    hasSelectedNodes,
    urlFiltersDebounced,
    testExecutionFilters,
    urlSort,
    sort,
    filterOptions,
    isFilterOptionsFetching,
  } = useTestResultsQueryContext({ enabled });

  const { data, isLoading, isFetching, isError, fetchNextPage, hasNextPage } =
    useGraphqlPagedQuery<
      ApiTestResultsTableData,
      ApiTestResultsTableDataPagedResponse
    >({
      queryKey: [
        TEST_RESULTS,
        envid,
        selectedIdsHash,
        DEFAULT_DAYS_BACK,
        urlFiltersDebounced,
        urlSort,
        search,
      ],
      query: getTestResultsTableDataQuery,
      variables: {
        testIds: selectedTestIds.length ? selectedTestIds : null,
        assetIds: selectedAssetIds.length ? selectedAssetIds : null,
        daysBack: DEFAULT_DAYS_BACK,
        testExecutionFilters,
        sort,
        search: search == '' ? null : search,
        limit: PAGE_SIZE,
      },
      select: (data) => data.latestTestExecutionsPaged,
      onError: onError,
      enabled: enabled && hasSelectedNodes,
      envId: envid,
    });

  const tableData = useMemo<TestResultsTableData[]>(() => {
    const distinctApiTests = distinctArrayBy(data, 'execution.subTestUniqueId');
    return distinctApiTests.map(parseApiTestResultTableData);
  }, [data]);

  return {
    tableData,
    filterOptions,
    isFetching: isLoading || isFetching,
    isFilterOptionsFetching,
    isError,
    fetchNextPage,
    hasNextPage,
  };
};

export default useTestResults;
